import {
  buildAnnualPremiumDiff,
  buildCreditsValueDiff,
  buildNoteDiff,
  buildTotalsBlockNoteDiff,
  parseColId,
} from "./buildPatchDiff";
import { financialsRowKinds } from "./types";

import type { FinancialsRow } from "./types";
import type { PatchFinancialsTableRequest, TotalsBlock } from "@platform/api";

interface CellChangeInput {
  data: FinancialsRow | undefined;
  colId: string | undefined;
  newValue: unknown;
  coverageByRowId: ReadonlyMap<string, string>;
  totalsBlocks: readonly TotalsBlock[];
}

const parseStringValue = (v: unknown): string | null =>
  v === "" || v == null ? null : String(v);

// Returns undefined when the value is invalid (NaN or below min).
const parseIntegerValue = (
  v: unknown,
  min: number,
): number | null | undefined => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  if (Number.isNaN(n) || n < min) return undefined;
  return Math.round(n);
};

/**
 * Resolves a cell value change into a PATCH diff, or `null` if the
 * change should be discarded (invalid input, unrecognized column, etc.).
 */
export const resolveCellChangeDiff = ({
  data,
  colId,
  newValue,
  coverageByRowId,
  totalsBlocks,
}: CellChangeInput): PatchFinancialsTableRequest | null => {
  if (!data || !colId) return null;

  const column = parseColId(colId);
  if (!column) return null;

  const coverageId = coverageByRowId.get(data.rowId);

  if (data.kind === financialsRowKinds.ANNUAL_PREMIUM) {
    if (coverageId === undefined) return null;
    const value = parseIntegerValue(newValue, 0);
    if (value === undefined) return null;
    return buildAnnualPremiumDiff(column, coverageId, value);
  }

  if (data.kind === financialsRowKinds.NOTES) {
    if (coverageId === undefined) return null;
    return buildNoteDiff(column, coverageId, parseStringValue(newValue));
  }

  if (data.kind === financialsRowKinds.TOTAL_NOTES) {
    return buildTotalsBlockNoteDiff(
      column,
      data.blockId ?? "default",
      parseStringValue(newValue),
      totalsBlocks,
    );
  }

  if (data.kind === financialsRowKinds.TOTAL_CREDITS) {
    const value = parseIntegerValue(newValue, 1);
    if (value === undefined) return null;
    return buildCreditsValueDiff(
      column,
      data.blockId ?? "default",
      value,
      totalsBlocks,
    );
  }

  return null;
};
